import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AlertTriangle, Sparkles, X, Lock } from 'lucide-react';

/**
 * Trial / subscription banner shown on top of admin dashboard.
 * Appears when:
 *   - Business is on trial → countdown banner
 *   - Business is frozen / trial expired → blocking banner with payment CTA
 */
export const TrialBanner = () => {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  const ts = user?.trial_status;
  if (!ts) return null;

  // Trial expired or frozen — blocking notice (cannot dismiss)
  if (ts.status === 'trial_expired' || ts.status === 'frozen' || user?.business_frozen) {
    return (
      <div
        className="rounded-lg border border-red-300 bg-red-50 p-4 mb-6 flex items-start gap-3"
        data-testid="trial-banner-expired"
      >
        <Lock className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
        <div className="flex-1">
          <p className="font-semibold text-red-900">
            {ts.status === 'trial_expired' ? 'Demo süreniz sona erdi' : 'Aboneliğiniz pasif'}
          </p>
          <p className="text-sm text-red-700 mt-0.5">
            Sistemi kullanmaya devam edebilmek için lütfen bir paket satın alın.
          </p>
          <div className="mt-3">
            <Link
              to="/admin/plan-upgrade"
              className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-4 py-2 rounded-md transition"
              data-testid="trial-banner-pay-link"
            >
              <Sparkles className="w-4 h-4" />
              Paket Seç & Ödeme Yap
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Trial running — soft countdown
  if ((ts.status === 'trial_active' || ts.status === 'trial_ending') && !dismissed) {
    const days = ts.days_remaining;
    const urgent = ts.status === 'trial_ending';
    const cls = urgent
      ? 'border-[#F5A524] bg-[#FFF4E5] text-[#92400E]'
      : 'border-[#BFDBFE] bg-[#EFF6FF] text-[#1E3A8A]';
    const Icon = urgent ? AlertTriangle : Sparkles;
    return (
      <div
        className={`rounded-lg border ${cls} p-4 mb-6 flex items-start gap-3`}
        data-testid="trial-banner-active"
      >
        <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${urgent ? 'text-amber-600' : 'text-blue-600'}`} />
        <div className="flex-1">
          <p className="font-semibold">
            {urgent
              ? `Demo sürenizin bitmesine ${days} gün kaldı`
              : `Ücretsiz deneme sürüyor — ${days} gün kaldı`}
          </p>
          <p className={`text-sm mt-0.5 ${urgent ? 'text-amber-800' : 'text-blue-800'}`}>
            {urgent
              ? 'Süre dolmadan paket seçerek hizmet kesintisiz devam etsin.'
              : '15 günlük deneme süreniz boyunca SMS gönderimi hariç tüm modülleri ücretsiz kullanabilirsiniz.'}
          </p>
          <div className="mt-3 flex items-center gap-3">
            <Link
              to="/admin/plan-upgrade"
              className={`inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg transition shadow-sm ${
                urgent
                  ? 'bg-[#F5A524] hover:bg-[#D97706] text-white'
                  : 'bg-[#2563EB] hover:bg-[#1E4ED8] text-white'
              }`}
              data-testid="trial-banner-upgrade-link"
            >
              Paketleri Görüntüle
            </Link>
            {!urgent && (
              <button
                onClick={() => setDismissed(true)}
                className="text-blue-700 hover:text-blue-900 text-xs"
                data-testid="trial-banner-dismiss"
              >
                Daha sonra
              </button>
            )}
          </div>
        </div>
        {!urgent && (
          <button
            onClick={() => setDismissed(true)}
            className="text-blue-500 hover:text-blue-700 shrink-0"
            aria-label="Kapat"
            data-testid="trial-banner-close"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }

  return null;
};
